import React, { useCallback } from "react";
import { AgGrid, Button } from "@jsq/ds/core";
import { type OverviewFilters, type OverviewSort } from "~/graphql/supergraphTypes";
import { BillingGeneratingModal } from "~/pages/Billing/components/BillingGeneratingModal";
import { useBillingDate } from "~/pages/Billing/contexts/BillingDateContext";
import { useBillingExport } from "~/pages/Billing/hooks/useBillingExport";

interface BillingClientOverviewExportProps {
  gridApi: AgGrid.GridApi | undefined;
}

type FilterInfo = {
  filterType: "text" | "set";
  filter?: string;
  values?: string[];
};

export const getBillingFilterVars = (
  filterModel: AgGrid.FilterModel | null,
): OverviewFilters[] | null => {
  if (!filterModel) {
    return null;
  }

  const queryFilters: OverviewFilters[] = [];

  Object.entries(filterModel).forEach(([field, filterInfo]: [string, FilterInfo]) => {
    // Handle text filter type
    if (filterInfo.filterType === "text" && filterInfo.filter) {
      queryFilters.push({ field: field, filterValues: [filterInfo.filter] });
    }
    // Handle set filter type
    else if (filterInfo.filterType === "set" && filterInfo.values !== undefined) {
      // Include empty arrays for set filters
      queryFilters.push({ field: field, filterValues: filterInfo.values });
    }
  });

  return queryFilters.length > 0 ? queryFilters : null;
};

export const getBillingSortVars = (gridApi: AgGrid.GridApi | null): OverviewSort[] | null => {
  if (!gridApi) {
    return null;
  }

  const sortModel = gridApi
    .getColumnState()
    .filter((col) => col.sort)
    .map((col) => ({
      field: col.colId,
      sortAscending: col.sort === "asc",
    }));

  return sortModel.length > 0 ? sortModel : null;
};

export const BillingClientOverviewExport: React.FC<BillingClientOverviewExportProps> = ({
  gridApi,
}) => {
  const { triggerExport, isExporting, modalProps } = useBillingExport();
  const { asOfDateString } = useBillingDate();

  const handleExportClick = useCallback(() => {
    if (!gridApi) return;

    const filters = getBillingFilterVars(gridApi.getFilterModel());
    const sorts = getBillingSortVars(gridApi);

    void triggerExport({
      exportType: "client_overview",
      filters: filters,
      sorts: sorts,
      asOfDate: asOfDateString,
    });
  }, [gridApi, triggerExport, asOfDateString]);

  if (!gridApi) {
    return null;
  }

  return (
    <>
      <Button
        variant="neutral"
        onClick={handleExportClick}
        isLoading={isExporting}
        data-testid="export-billing-data-button"
      >
        Export View
      </Button>

      <BillingGeneratingModal {...modalProps} />
    </>
  );
};
